/** Screen-entry loaders (mandate §16) — the one place a generated screen's async state gets its kick.
 *  Generated views cannot carry hooks (DOM parity), so entry-triggered fetches live here: the Settings
 *  profile section reads me.profile.get on first arrival, whichever path led in (nav click or deep link).
 */
import { useEffect } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';

export function Loaders({ m }: { m: Partial<ViewModel> }) {
  // App-wide, not Settings-only: the header avatar is profile state, and a reload on ANY screen must show
  // the trader's own photo rather than the initials fallback. profileLoad guards itself against re-entry.
  const wantsProfile = !m.profileState && !!m.profileLoad;
  useEffect(() => {
    if (wantsProfile) m.profileLoad?.();
  }, [wantsProfile, m]);
  // The risk state feeds the Prepare cards, the Risk Rules screen, the Risk Profile screen AND the arm call's
  // caps — every screen that renders or ships a limit loads the server's own values first (mandate §1).
  const wantsRisk = (!!m.isPrepare || !!m.isRiskRules || !!m.isRiskProfile || !!m.isTradeHub) && !m.riskState;
  useEffect(() => {
    if (wantsRisk) m.riskLoad?.();
  }, [wantsRisk, m]);
  // The watchlist renders on Briefing and Prepare and feeds the signal scan — hydrated from the server once.
  const wantsWl = (!!m.isBriefing || !!m.isPrepare || !!m.isSignals) && !!m.wlHydrate;
  useEffect(() => {
    if (wantsWl) m.wlHydrate?.();
  }, [wantsWl, m]);
  // Settings' notification switches are me.preferences state — hydrated when the screen shows them.
  const wantsPrefs = !!m.isSettings && !!m.prefsHydrate;
  useEffect(() => {
    if (wantsPrefs) m.prefsHydrate?.();
  }, [wantsPrefs, m]);

  // The strategies playbook backs the Strategies screen's toggles and created cards.
  const wantsStrat = !!m.isStrategies && !!m.stratHydrate;
  useEffect(() => {
    if (wantsStrat) m.stratHydrate?.();
  }, [wantsStrat, m]);
  // Widget visibility backs the Widgets screen's grid and its Edit Dashboard sheet.
  const wantsDashW = !!m.isWidgets && !!m.dashWHydrate;
  useEffect(() => {
    if (wantsDashW) m.dashWHydrate?.();
  }, [wantsDashW, m]);
  return null;
}
